import type { AdUnitDefinition, PrebidJS, Bid } from 'prebid.js'

import type { AdType, AdTargeting } from '@/modules/adv/types'
import { AdProviderTypes, type PlatformType } from '@/types'
import type { StartRumEvent } from '@/types/logger.types'
import { timer } from '@/utils/helpers'

import { type ActiveCmps, hasActiveCmp } from '../../consent-manager'
import { isOwnerTrackedAuction } from '../../providers/shared/owner-tracker'
import { AdConsoleLogger } from '../../utils/ad-console-logger'
import { createRumTracker } from '../../utils/create-rum-tracker'
import { APS_ACCOUNT_ID } from '../aps/types'
import { BIDDER_SETTINGS, BIDDER_TIMEOUT, buildOrtb2, getPrebidConfig } from './config'
import { isBidderAllowedInCountry } from './utils/country-rules'
import { getSspAuctionId } from './utils/ssp-auction-id'

const AUCTION_TIMEOUT_MS = 2_000
const GET_USER_SIGNALS_TIMEOUT = 100
const AUCTION_RESULT_TTL_CAP_MS = 60_000

import type {
    AdAuctionSource,
    HbAuctionContext,
    HbAdUnitDefinition,
    HbBidderConfig,
    HbBidderContext,
    HbSiteGameInfo,
    HeaderBiddingAuctionResult,
    HeaderBiddingInterface,
    HeaderBiddingOptions,
    PrebidAuctionTargeting,
    RenderAdOptions,
} from './types'
import { hasValidVastMarkup } from './validate-video-bid'

function resolveBids({
    bids,
    placement,
    platform,
    adSource,
    pgAuid,
    countryCode,
    requestContext,
    sspHost,
    renderersBase,
    hasConsent,
}: {
    bids: HbBidderConfig[]
    placement: AdType
    platform: PlatformType
    adSource: HeaderBiddingOptions['adSource']
    pgAuid?: string
    countryCode?: string
    requestContext?: HbBidderContext['requestContext']
    sspHost: string
    renderersBase: string
    hasConsent: boolean
}): NonNullable<AdUnitDefinition['bids']> {
    const context: HbBidderContext = {
        placement,
        platform,
        adSource,
        pgAuid,
        countryCode,
        requestContext,
        sspHost,
        renderersBase,
    }
    const resolved = bids
        .filter((bid) => isBidderAllowedInCountry(bid.countries, countryCode))
        .map((bid) => ({
            ...bid,
            params: typeof bid.params === 'function' ? bid.params(context) : bid.params,
            ortb2Imp: bid.ortb2Imp,
        }))

    return resolved.filter(
        (bid): bid is (typeof resolved)[number] & { params: Record<string, unknown> } =>
            bid.params !== undefined && (hasConsent || bid.bidder === 'playgama'),
    )
}
export class HeaderBidding implements HeaderBiddingInterface {
    private prebid: PrebidJS | undefined

    private readonly auctions = new Map<string, HeaderBiddingAuctionResult>()

    private readonly auctionContexts = new Map<string, { placement: AdType; provider: AdProviderTypes }>()

    private readonly pendingRenders = new Map<string, (rendered: boolean) => void>()

    private currentTargeting: AdTargeting = {}

    private currentGameInfo: HbSiteGameInfo | null = null

    private readonly adConsoleLogger = new AdConsoleLogger('hb')

    initialized: Promise<{ success: boolean; error?: string }>

    private readonly hasConsent: boolean

    get adSource(): AdAuctionSource {
        return this.options.adSource
    }

    constructor(
        private options: HeaderBiddingOptions,
        private readonly activeCmps: ActiveCmps,
        private readonly startRumEvent?: StartRumEvent,
    ) {
        this.hasConsent = hasActiveCmp(activeCmps)
        const { resolve, promise } = Promise.withResolvers<{ success: boolean; error?: string }>()
        this.initialized = promise

        this.adConsoleLogger.debug('start header bidding init')
        import('./prebidjs').then(({ prebid }) => {
            this.prebid = prebid

            Object.assign(this.prebid.bidderSettings, BIDDER_SETTINGS)
            this.prebid.setConfig(getPrebidConfig(this.options, activeCmps))
            this.prebid.setBidderConfig(
                { bidders: ['aps'], config: { aps: { accountID: APS_ACCOUNT_ID, debug: this.options.apsDebug } } },
                true,
            )
            this.applyTargeting()

            this.prebid.onEvent('bidRequested', (bidderRequest) => {
                const adUnitCode = bidderRequest.bids[0]?.adUnitCode
                const ctx = (adUnitCode && this.auctionContexts.get(adUnitCode)) || undefined
                this.options.logEvent({
                    event: 'custom_event',
                    eventName: 'ad_hb_bidder_request',
                    adPlacement: ctx?.placement,
                    adProvider: ctx?.provider,
                    adUnit: adUnitCode,
                    adHbBidder: bidderRequest.bidderCode ?? undefined,
                })
            })

            this.prebid.onEvent('adRenderSucceeded', ({ bid }) => {
                this.adConsoleLogger.debug('Ad render succeeded', { bidder: bid.bidder, adUnit: bid.adUnitCode })
                const auctionResult = bid.auctionId && this.auctions.get(bid.auctionId)
                if (auctionResult) {
                    auctionResult.renderedBid = bid
                    auctionResult.onBidRendered?.()
                }
                if (bid.auctionId) {
                    this.auctions.delete(bid.auctionId)
                }
                if (bid.adId && !(bid.bidder === 'playgama' && (bid.renderer?.renderNow || bid.renderer?.url))) {
                    this.pendingRenders.get(bid.adId)?.(true)
                }
            })

            this.prebid.onEvent('adRenderFailed', ({ bid, adId, reason, message }) => {
                if (adId) {
                    this.pendingRenders.get(adId)?.(false)
                }
                this.adConsoleLogger.error('Ad render failed', { reason, message, bidder: bid?.bidder })
                const adUnitCode = bid?.adUnitCode
                const ctx =
                    (bid?.auctionId && this.auctions.get(bid.auctionId)?.context) ||
                    (adUnitCode && this.auctionContexts.get(adUnitCode)) ||
                    undefined
                if (bid?.auctionId) {
                    this.auctions.delete(bid.auctionId)
                }
                this.options.logEvent({
                    event: 'custom_event',
                    eventName: 'ad_hb_render_failed',
                    adUnit: bid?.adUnitCode,
                    adPlacement: ctx?.placement,
                    adProvider: ctx?.provider,
                    adHbBidder: bid?.bidder,
                    dimension3: `${reason}: ${message}`,
                })
            })

            window.addEventListener('message', (e) => {
                try {
                    const { pgSSP } = e.data as { pgSSP: { event: string; payload: Record<string, unknown> } }
                    if (pgSSP && pgSSP.event === 'gtm') {
                        window.dataLayer.push(pgSSP.payload)
                    }
                } catch {}
            })

            this.adConsoleLogger.debug('header bidding init completed')
            this.getUserSignals().then(resolve)
        })
    }

    getUserSignals(): Promise<{ success: boolean; error?: string }> {
        this.adConsoleLogger.debug('start getUserSignals')

        if (!this.prebid) {
            this.adConsoleLogger.debug('getUserSignals failed: prebid not initialized')
            return Promise.resolve({ error: 'prebid not initialized', success: false })
        }

        const result = this.prebid
            .getUserIdsAsync()
            .then((data) => {
                const modules = Object.keys(data).filter((k) => data[k] !== null && data[k] !== undefined)
                const modulesStr = modules.join(',') || 'none'

                this.adConsoleLogger.debug(`user ID modules loaded: ${modulesStr}`)
                this.options.logEvent({
                    event: 'custom_event',
                    eventName: 'ad_hb_user_ids',
                    dimension1: modulesStr,
                })

                return { success: true }
            })
            .catch((error: unknown) => {
                this.adConsoleLogger.error('getUserSignals failed', error)
                return { success: false, error: error instanceof Error ? error.message : String(error) }
            })

        return Promise.race([result, timer(GET_USER_SIGNALS_TIMEOUT, { value: { success: false, error: 'timeout' } })])
    }

    public updateTargeting(targeting: AdTargeting): void {
        this.currentTargeting = { ...targeting }
        this.applyTargeting()
    }

    public updateGameInfo(gameInfo: HbSiteGameInfo | null): void {
        this.currentGameInfo = gameInfo
        this.applyTargeting()
    }

    markAuctionResultUsed(auctionResult: HeaderBiddingAuctionResult | undefined): void {
        const bid = auctionResult?.renderedBid
        // banner bids are marked `rendered` and billed by prebid's renderAd itself; video plays
        // through IMA bypassing prebid, so it must be marked (and billed) manually
        if (bid && bid.status !== 'rendered') {
            this.prebid?.markWinningBidAsUsed({ adId: bid.adId, events: true })
        }
    }

    async runAuction(
        adUnit: HbAdUnitDefinition,
        context: HbAuctionContext,
    ): Promise<HeaderBiddingAuctionResult | undefined> {
        if (!this.prebid) {
            return undefined
        }

        const resolvedAdUnit = {
            ...adUnit,
            // Owner-tracked auctions: the playgama adapter reads this marker to
            // skip embedding tracker scripts in the creative markup — the
            // provider base fires the SSP trackers itself (attachOwnerTrackers).
            // The adapter strips the marker from the SSP request payload.
            ...(isOwnerTrackedAuction(context.provider, context.placement)
                ? { ortb2Imp: { ...adUnit.ortb2Imp, ext: { ...adUnit.ortb2Imp?.ext, pg_owner_tracking: 1 } } }
                : {}),
            bids: resolveBids({
                bids: adUnit.bids,
                sspHost: this.options.sspHost,
                renderersBase: this.options.renderersBase,
                placement: context.placement,
                platform: this.options.platform,
                adSource: this.options.adSource,
                pgAuid: this.options.pgAuid,
                countryCode: this.options.countryCode,
                requestContext: context.requestContext,
                hasConsent: this.hasConsent,
            }),
        }

        this.adConsoleLogger.debug(`running auction for ${context.placement} (${context.provider})`)

        this.auctionContexts.set(resolvedAdUnit.code, context)
        const auctionTracker = this.startRumEvent ? createRumTracker('hb-auction', this.startRumEvent) : null

        const requestResult = await Promise.race([
            this.prebid.requestBids({ adUnits: [resolvedAdUnit], timeout: BIDDER_TIMEOUT }),
            timer(AUCTION_TIMEOUT_MS),
        ])
        this.auctionContexts.delete(resolvedAdUnit.code)

        if (!requestResult) {
            auctionTracker?.send({
                status: 'error',
                dimension1: context.provider,
                dimension2: context.placement,
                dimension3: 'timeout',
            })
            this.options.logEvent({
                event: 'custom_event',
                eventName: 'ad_hb_auction_timeout',
                adPlacement: context.placement,
                adProvider: context.provider,
                adUnit: adUnit.code,
            })
            return undefined
        }

        const { auctionId } = requestResult

        const winningBid = this.prebid.getHighestCpmBids(resolvedAdUnit.code)?.[0]

        auctionTracker?.send({
            status: winningBid ? 'success' : 'empty',
            dimension1: context.provider,
            dimension2: context.placement,
            dimension3: winningBid?.bidder ?? 'no-bidder',
        })
        const targeting = this.prebid.getAdserverTargetingForAdUnitCode(resolvedAdUnit.code)

        // Some bidders report mediaType: 'video' with no actual VAST markup (just a tracking
        // pixel/script) — rendering that as video fails outright, losing the placement. The
        // mediaType field itself can't be reassigned to 'banner' (Bid['mediaType'] only allows
        // 'video' | 'audio'), so consumers (e.g. PrebidDirectProvider) check
        // hasValidVastMarkup themselves before treating a bid as video. We just log it here so we
        // can track how often it happens.
        if (winningBid?.mediaType === 'video' && !hasValidVastMarkup(winningBid.vastXml)) {
            this.adConsoleLogger.error('Invalid video bid (no VAST markup), will render as banner', {
                bidder: winningBid.bidder,
                adUnit: resolvedAdUnit.code,
            })
            this.options.logEvent({
                event: 'custom_event',
                eventName: 'ad_hb_invalid_format_bid',
                adPlacement: context.placement,
                adProvider: context.provider,
                adUnit: adUnit.code,
                dimension3: `resulted winningBid vastXml: ${winningBid.vastXml?.slice(0, 150)}`,
                adHbBidder: winningBid.bidder,
            })
        }

        const auctionResult = this.createAuctionResult({ auctionId, targeting, winningBid, context })

        this.adConsoleLogger.debug(`auction result for ${context.placement}`, {
            adUnit: adUnit.code,
            bidder: auctionResult.winningBid?.bidder ?? 'no-bidder',
            cpm: auctionResult.winningBid?.cpm,
            auctionId,
            sspAuctionId: getSspAuctionId(auctionResult.winningBid),
        })

        this.options.logEvent({
            event: 'custom_event',
            eventName: 'ad_hb_auction',
            adPlacement: context.placement,
            adProvider: context.provider,
            adUnit: adUnit.code,
            adHbCpmUsd: auctionResult.winningBid?.cpm ?? undefined,
            adHbBidder: auctionResult.winningBid?.bidder || 'no-bidder',
            adHbRequestId: auctionResult.winningBid?.requestId || undefined,
            dimension1: auctionResult.winningBid?.mediaType,
            dimension2:
                winningBid?.bidder === 'playgama' && typeof winningBid.meta.demandPartner === 'string'
                    ? winningBid.meta.demandPartner
                    : undefined,
        })

        this.prebid
            .getBidResponsesForAdUnitCode(resolvedAdUnit.code)
            .bids.filter((b) => b.auctionId === auctionId)
            .forEach((bid) => {
                const sspAuctionId = getSspAuctionId(bid)
                if (sspAuctionId) {
                    // logged for every playgama response, not just the winner: a bid that
                    // lost the prebid auction still has an SSP auction behind it
                    this.adConsoleLogger.debug(`ssp auction ${sspAuctionId}`, {
                        placement: context.placement,
                        cpm: bid.cpm,
                        requestId: bid.requestId,
                    })
                }
                this.options.logEvent({
                    event: 'custom_event',
                    eventName: 'ad_hb_bidder_response',
                    adPlacement: context.placement,
                    adProvider: context.provider,
                    adUnit: adUnit.code,
                    adHbCpmUsd: bid.cpm,
                    adHbBidder: bid.bidder,
                    adHbRequestId: bid.requestId || undefined,
                    dimension1: String(bid.timeToRespond),
                })
            })

        return auctionResult
    }

    private applyTargeting(): void {
        this.prebid?.setConfig({
            ortb2: buildOrtb2({
                activeCmps: this.activeCmps,
                schainNode: this.options.schainNode,
                targeting: this.currentTargeting,
                gameInfo: this.currentGameInfo,
            }),
        })
    }

    private createAuctionResult({
        auctionId,
        targeting,
        winningBid,
        context,
    }: {
        auctionId?: string
        targeting?: PrebidAuctionTargeting
        winningBid?: Bid
        context?: { placement: AdType; provider: AdProviderTypes }
    }): HeaderBiddingAuctionResult {
        const auctionResult: HeaderBiddingAuctionResult = {
            auctionId,
            targeting,
            winningBid,
            timestamp: Date.now(),
            context,
        }

        if (auctionId && winningBid) {
            this.auctions.set(auctionId, auctionResult)
            window.setTimeout(
                () => {
                    if (this.auctions.delete(auctionId)) {
                        this.options.logEvent({
                            event: 'custom_event',
                            eventName: 'ad_hb_auction_expired',
                            adPlacement: context?.placement,
                            adProvider: context?.provider,
                            adHbBidder: winningBid.bidder,
                        })
                    }
                },
                Math.min(winningBid.ttl * 1000, AUCTION_RESULT_TTL_CAP_MS),
            )
        }

        return auctionResult
    }

    /**
     * Render the winning bid into the provided container element.
     * Banner/HTML creatives: sandboxed iframe + pbjs.renderAd.
     */
    async renderAd(bid: Bid, container: HTMLElement, options?: RenderAdOptions): Promise<boolean> {
        if (!this.prebid) {
            this.adConsoleLogger.error('Prebid not initialized')
            return false
        }

        // Some bidders report mediaType: 'video' with a renderer configured but no actual VAST
        // markup (just a tracking pixel/script) — handing that to the renderer fails outright.
        // Fall back to the plain banner iframe path below, using bid.ad, instead of losing the
        // placement.
        const isValidVideoBid =
            (bid.mediaType === 'video' && hasValidVastMarkup(bid.vastXml)) || bid.mediaType !== 'video'

        // Renderer bids (outstream video + h5 creatives): let Prebid drive the pipeline.
        // isRendererRequired checks for renderer.url or renderer.renderNow,
        // and executeRenderer calls renderer.render(bid) internally, which
        // also fires the adRenderSucceeded event.
        if (isValidVideoBid && (bid.renderer?.renderNow || bid.renderer?.url)) {
            try {
                // Clear stale content from a previous render so the renderer
                // always gets an empty container (critical for refresh: without
                // this the old outstream player blocks the new one).
                container.innerHTML = ''
                if (bid.bidder === 'playgama' && bid.renderer) {
                    bid.renderer.config = {
                        ...options,
                        onRendered: () => {
                            this.pendingRenders.get(bid.adId)?.(true)
                            options?.onRendered?.()
                        },
                        onError: (message: string) => {
                            this.pendingRenders.get(bid.adId)?.(false)
                            options?.onError?.(message)
                        },
                    }
                } else if (options && bid.renderer) {
                    bid.renderer.config = {
                        ...options,
                        onError: (message: string) => {
                            this.pendingRenders.get(bid.adId)?.(false)
                            options.onError?.(message)
                        },
                    }
                }
                const rendered = this.trackRenderResult(bid.adId)
                pbjs.renderAd(document, bid.adId)
                return await rendered
            } catch (error) {
                this.adConsoleLogger.error('Failed to render ad:', error)
                return false
            }
        }
        // Banner bids: write markup into a sandboxed iframe.
        try {
            if (!bid.ad) {
                throw new Error('No ad markup')
            }

            container.innerHTML = ''

            const iframe = document.createElement('iframe')
            iframe.width = String(bid.width)
            iframe.height = String(bid.height)
            iframe.style.width = `${bid.width}px`
            iframe.style.height = `${bid.height}px`
            iframe.style.border = '0'
            iframe.style.display = 'block'
            iframe.setAttribute('scrolling', 'no')
            container.appendChild(iframe)

            const frameDocument = iframe.contentWindow?.document
            if (!frameDocument) {
                throw new Error('Unable to access iframe document for direct render')
            }

            const adFrameTemplate = `
<!doctype html>
<html>
    <head>
     <script>
        window.addEventListener('message', function(event) {
            window.parent.postMessage(event.data, '*')
        })
    </script>
    </head>
    <body style="margin:0"></body>
</html>
`

            frameDocument.open()
            frameDocument.write(adFrameTemplate)
            frameDocument.close()

            const rendered = this.trackRenderResult(bid.adId)
            pbjs.renderAd(frameDocument, bid.adId)

            return await rendered
        } catch (error) {
            this.adConsoleLogger.error('Failed to render ad:', error)
            return false
        }
    }

    private trackRenderResult(adId: string): Promise<boolean> {
        const { promise, resolve } = Promise.withResolvers<boolean>()
        this.pendingRenders.set(adId, (rendered) => {
            this.pendingRenders.delete(adId)
            resolve(rendered)
        })
        return promise
    }
}
